import { formatClock, formatRelative } from '../lib/time';
import type { DateRange, FrictionSubtype, InterruptionSource, Ping, PingType, RescueSession } from '../types';

type HistoryTabProps = {
  historyRange: DateRange;
  onChangeHistoryRange: (value: DateRange) => void;
  typeFilters: PingType[];
  onToggleTypeFilter: (type: PingType) => void;
  allTypes: PingType[];
  filteredHistory: Ping[];
  rescues: RescueSession[];
  onOpenPing: (ping: Ping) => void;
  rangeLabel: (range: DateRange) => string;
  pingTypeLabel: (type: PingType) => string;
  subtypeLabel: (subtype: FrictionSubtype) => string;
  interruptionLabel: (source: InterruptionSource) => string;
  rescueUsedLabel: string;
  emptyLabel: string;
};

export function HistoryTab({
  historyRange,
  onChangeHistoryRange,
  typeFilters,
  onToggleTypeFilter,
  allTypes,
  filteredHistory,
  rescues,
  onOpenPing,
  rangeLabel,
  pingTypeLabel,
  subtypeLabel,
  interruptionLabel,
  rescueUsedLabel,
  emptyLabel
}: HistoryTabProps) {
  return (
    <section>
      <div className="history-controls">
        <div className="filters history-ranges">
          {(['today', '7d', '30d', 'all'] as DateRange[]).map((r) => (
            <button key={r} className={historyRange === r ? 'active' : ''} onClick={() => onChangeHistoryRange(r)}>
              {rangeLabel(r)}
            </button>
          ))}
        </div>
        <div className="chips history-types">
          {allTypes.map((type) => {
            const enabled = typeFilters.includes(type);
            return (
              <button key={type} className={enabled ? 'active' : ''} onClick={() => onToggleTypeFilter(type)}>
                {pingTypeLabel(type)}
              </button>
            );
          })}
        </div>
      </div>
      <div className="list">
        {filteredHistory.map((p) => (
          <button key={p.id} className="row history-entry" onClick={() => onOpenPing(p)}>
            <div className="history-entry-topline">
              <span className="history-entry-kicker">{formatRelative(p.ts_ms)}</span>
              <span className="history-entry-time">{formatClock(p.ts_ms)}</span>
            </div>
            <div className="history-entry-title">{pingTypeLabel(p.type)}</div>
            <div className="history-entry-meta">
              {p.friction_started_ms && (
                <span>{`Stuck for ${Math.max(1, Math.round((p.ts_ms - p.friction_started_ms) / 60000))}m before ping`}</span>
              )}
              {p.subtype && <span>{`Subtype: ${subtypeLabel(p.subtype)}`}</span>}
              {p.interruption_source && <span>{`Interrupted by: ${interruptionLabel(p.interruption_source)}`}</span>}
              {rescues.some((r) => r.ping_id === p.id && r.ts_start_ms - p.ts_ms <= 5 * 60 * 1000) && (
                <span className="history-entry-callout">{rescueUsedLabel}</span>
              )}
            </div>
          </button>
        ))}
        {filteredHistory.length === 0 && <div className="card empty-state-card">{emptyLabel}</div>}
      </div>
    </section>
  );
}
